/* eslint-disable no-param-reassign */
// `.current` writes below are refs bridging to the botLoading handler and upload
// analytics. See .claude/context/orchestrator-flows.md
import {
  MESSAGE_ORIGIN_UNSPECIFIED,
  RETRY_MESSAGE,
  LIVE_AGENT_IDLE_DISCONNECTED_MESSAGE,
  LIVE_AGENT_ERROR_MESSAGE,
  UPLOAD_ERROR_MESSAGES,
} from '../../shared/constants';
import { createActionId } from '../../platform/session/session';
import { getMcvisID, getSvocId } from '../../shared/utils/window-utils';
import { getContentModeration, getImageDimensions } from '../../shared/utils/common';
import { redactSensitiveContent } from '../../shared/utils/redaction';
import { isImageUrlValid } from '../../shared/utils/file-upload-utils';
import { emit } from '../../platform/events/lifecycle';
import { noticeError } from '../../platform/telemetry/newRelic';
import { MessageType, createMessage } from '../../shared/message';
import { logger } from '../../shared/utils/logger';

/** A user question: redaction gate, image moderation, then the live agent or the Planner bot.
 * `input` holds userQuestion plus optional origin, analytics, pageType and retry-image fields. */
export const runAskUserQuestion = async (input, ctx) => {
  const {
    userQuestion,
    messageOrigin = MESSAGE_ORIGIN_UNSPECIFIED,
    entryLocationForAnalytics,
    pageType,
    retryStorageLink,
    retryImageId,
  } = input;
  const {
    session,
    hosts,
    imageData,
    getIsEscalated,
    sessionIDRef,
    actionIDRef,
    imageMetaRef,
    messageHistoryRef,
    openDrawer,
    messages,
    setIsSubmitting,
    setUploadError,
    setHistoryCleared,
    saveMessage,
    submitUserQuestionToApi,
    sendAgentMessage,
    sendAgentImage,
    updateLastActivity,
    handleIdleDisconnect,
    checkIdleExpired,
    clearImage,
    broadCastMessageUpdate,
  } = ctx;

  const saveRetryMessage = async (skip) => {
    if (!skip && messages.at(-1)?.type === 'user') {
      return saveMessage(RETRY_MESSAGE, { waitForStorage: false });
    }
    return Promise.resolve();
  };

  // Centralized sensitive-data gate — blocks card/SSN regardless of caller.
  const { redacted, hasSensitive, error: redactError } = redactSensitiveContent(userQuestion);
  if (hasSensitive) {
    setIsSubmitting(false);
    return { blocked: true, redacted, error: redactError };
  }

  const sessionID = session.sessionId;
  // The single source of truth for actionID in the rest of this flow.
  const actionID = createActionId(session.sessionId);
  sessionIDRef.current = sessionID;
  actionIDRef.current = actionID;
  let imageId = retryImageId || null;
  let storageLink = retryStorageLink || null;
  imageMetaRef.current = null;

  if (imageData && !getIsEscalated()) {
    imageMetaRef.current = {
      imageOrigin: imageData.imageOrigin,
      imageFileType: imageData.imageFileType,
      fileSize: imageData.fileSize
    };
    const uploadContext = {
      userContext: {
        userIdentities: {
          mcvisId: getMcvisID(),
          svocId: getSvocId()
        },
        inReplyToChatMessageId: '',
        caseId: '',
        zipCode: '',
        deviceType: '',
        actionId: actionID,
        sessionId: sessionID,
        sender: '',
        clientMessageId: '',
        language: '',
        storeId: '',
        conversationId: '',
      },
      pageContext: {
        pageType: pageType ?? '',
        referrerPage: '',
        timestamp: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z'),
        url: ''
      },
    };
    let moderationResult;
    try {
      moderationResult = await getContentModeration(hosts?.apionline, imageData.file, uploadContext);
    } catch (err) {
      setUploadError(UPLOAD_ERROR_MESSAGES.GENERIC_ERROR);
      ctx.sendImageUploadAnalytics(sessionID, actionID, 'failure', userQuestion);
      setIsSubmitting(false);
      return false;
    }
    if (!moderationResult.moderationStatus) {
      setUploadError(UPLOAD_ERROR_MESSAGES.GENERIC_ERROR);
      ctx.sendImageUploadAnalytics(sessionID, actionID, 'failure', userQuestion);
      setIsSubmitting(false);
      return false;
    }
    if (moderationResult.moderationStatus !== 'VALID') {
      clearImage();
      setUploadError(UPLOAD_ERROR_MESSAGES.UNSUPPORTED_CONTENT_PROPRIETY);
      ctx.sendImageUploadAnalytics(sessionID, actionID, 'failure', userQuestion);
      setIsSubmitting(false);
      return false;
    }
    imageId = moderationResult.imageId;
    storageLink = moderationResult.storageLink;
  } else if (imageData && getIsEscalated()) {
    const userMessagePayload = createMessage(MessageType.USER, userQuestion, {
      messageOrigin,
      isImageAttached: true,
      ...(imageData?.imageOrigin && { imageOrigin: imageData.imageOrigin }),
      ...(imageData?.file?.name && { imageFileName: imageData.file.name }),
      ...(imageData?.aspectRatio && (() => {
        const { width, height } = getImageDimensions(imageData.aspectRatio, messageHistoryRef.current?.clientWidth);
        return { imageWidth: width, imageHeight: height };
      })()),
    });
    try {
      await saveMessage(userMessagePayload, { waitForStorage: false });
      await sendAgentImage(userQuestion, imageData.file, actionID);
      clearImage();
    } catch (err) {
      // The block reconciles and retries internally; a rejection here means it could not
      // reach a human at all, so say so rather than failing silently.
      logger.error(err);
      await saveMessage(LIVE_AGENT_ERROR_MESSAGE).catch((err1) => { logger.error(err1); });
    } finally {
      updateLastActivity();
      setIsSubmitting(false);
      broadCastMessageUpdate();
    }
    return Promise.resolve();
  }

  // Direct downward call, not a bus hop. See
  // .claude/context/composition-topology.md's "Context direction matters"
  openDrawer();

  setHistoryCleared(false);

  const userMessagePayload = createMessage(MessageType.USER, userQuestion, {
    messageOrigin,
    isImageAttached: !!imageData || !!retryStorageLink,
    ...(imageData?.imageOrigin && { imageOrigin: imageData.imageOrigin }),
    ...(imageData && { imageFileName: imageData.file?.name || 'user-uploaded image' }),
    ...(storageLink && { storageLink }),
    ...(imageId && { imageId }),
    ...(imageData?.aspectRatio && (() => {
      const { width, height } = getImageDimensions(imageData.aspectRatio, messageHistoryRef.current?.clientWidth);
      return { imageWidth: width, imageHeight: height };
    })()),
  });

  // ---- Idle TTL check — if escalation expired, disconnect and route to bot ----
  const isIdleExpired = checkIdleExpired();
  if (getIsEscalated() && isIdleExpired) {
    await handleIdleDisconnect();
    await saveMessage(LIVE_AGENT_IDLE_DISCONNECTED_MESSAGE, { waitForStorage: false })
      .catch((err) => { logger.error(err); });
  }

  // ---- Live-agent mode: route through Sprinklr ----
  // sendAgentMessage owns reconnection. See .claude/context/live-agent.md
  if (getIsEscalated()) {
    logger.log('[MAA] liveAgentHandoff detected — routing to the live-agent block');
    return saveMessage(userMessagePayload, { waitForStorage: false })
      .then(() => sendAgentMessage(userQuestion, actionID))
      .catch((err) => {
        logger.error(err);
        saveMessage(LIVE_AGENT_ERROR_MESSAGE).catch((err1) => { logger.error(err1); });
      })
      .finally(() => {
        updateLastActivity();
        broadCastMessageUpdate();
        setIsSubmitting(false);
      });
  }

  logger.log('[MAA] askUserQuestion — routing to BOT (isEscalated=false)');

  // ---- Normal bot flow ----
  return saveRetryMessage(isIdleExpired)
    .then(async () => {
      if (storageLink) {
        const imageLoaded = await isImageUrlValid(storageLink);
        if (!imageLoaded) {
          userMessagePayload.imageLoadFailed = true;
        }
      } else if (imageData) {
        userMessagePayload.imageLoadFailed = true;
      }
    })
    // The request never waits for storage. See .claude/context/orchestrator-flows.md.
    .then(() => saveMessage(userMessagePayload, { waitForStorage: false }))
    .then((savedUserMessage) => {
      if (imageData) {
        clearImage();
      }
      return submitUserQuestionToApi({
        userQuestion: savedUserMessage.content,
        timestamp: savedUserMessage.timestamp,
        imageId,
        storageLink,
        actionID,
      });
    })
    .finally(() => {
      emit('magic-apron-assistant.question-ask', {
        userQuestion,
        messageOrigin,
        entryLocationForAnalytics,
        pageType,
        sessionID,
        actionID
      }, { frame: 'parent' });
    })
    .catch((err) => {
      // Nothing was sent, so no bot turn will clear the composer's busy state.
      // See .claude/context/orchestrator-flows.md
      logger.error(err);
      noticeError(err, { maaOperation: 'askUserQuestion' });
      setIsSubmitting(false);
    });
};
